import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/lib/auth';
import ProtectedRoute from '@/components/ProtectedRoute';
import AdminLayout from '@/components/AdminLayout';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Partners from '@/pages/Partners';
import Bookings from '@/pages/Bookings';
import Payments from '@/pages/Payments';
import Landing from '@/pages/Landing';
import PrateekAI from '@/pages/PrateekAI';
import Compliance from '@/pages/Compliance';
import Terms from '@/pages/legal/Terms';
import Privacy from '@/pages/legal/Privacy';
import PartnerTerms from '@/pages/legal/PartnerTerms';
import SafetyPolicy from '@/pages/legal/SafetyPolicy';
import ErrorBoundary from '@/components/ErrorBoundary';

// Partner App (Phase 2)
import ProtectedPartnerRoute from '@/components/partner/ProtectedPartnerRoute';
import PartnerLayout from '@/components/partner/PartnerLayout';
import PartnerLogin from '@/pages/partner/Login';
import PartnerSignup from '@/pages/partner/Signup';
import PartnerDashboard from '@/pages/partner/Dashboard';
import PartnerProfile from '@/pages/partner/Profile';
import PartnerBookings from '@/pages/partner/Bookings';
import PartnerEarnings from '@/pages/partner/Earnings';

// Customer App (Phase 3)
import ProtectedCustomerRoute from '@/components/customer/ProtectedCustomerRoute';
import CustomerLayout from '@/components/customer/CustomerLayout';
import CustomerLogin from '@/pages/customer/Login';
import CustomerSignup from '@/pages/customer/Signup';
import CustomerHome from '@/pages/customer/Home';
import CustomerPartnerProfile from '@/pages/customer/PartnerProfile';
import CustomerBookingPage from '@/pages/customer/Booking';
import CustomerBookings from '@/pages/customer/Bookings';
import PaymentResult from '@/pages/customer/PaymentResult';

import { Toaster } from '@/components/ui/sonner';

function App() {
  return (
    <div className="App min-h-screen bg-white">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public landing page (Phase 3) */}
            <Route path="/" element={<Landing />} />

            {/* Public legal pages */}
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/partner-terms" element={<PartnerTerms />} />
            <Route path="/safety-policy" element={<SafetyPolicy />} />

            <Route path="/admin/login" element={<Login />} />
            <Route
              path="/admin"
              element={(
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              )}
            >
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="partners" element={<Partners />} />
              <Route path="bookings" element={<Bookings />} />
              <Route path="payments" element={<Payments />} />
              <Route path="prateek-ai" element={<PrateekAI />} />
              <Route path="compliance" element={<Compliance />} />
            </Route>

            {/* Partner App (Phase 2) */}
            <Route path="/partner" element={<Navigate to="/partner/login" replace />} />
            <Route
              path="/partner/login"
              element={(
                <ErrorBoundary scope="partner" homePath="/">
                  <PartnerLogin />
                </ErrorBoundary>
              )}
            />
            <Route
              path="/partner/signup"
              element={(
                <ErrorBoundary scope="partner" homePath="/">
                  <PartnerSignup />
                </ErrorBoundary>
              )}
            />
            <Route
              element={(
                <ErrorBoundary scope="partner" homePath="/partner/login">
                  <ProtectedPartnerRoute>
                    <PartnerLayout />
                  </ProtectedPartnerRoute>
                </ErrorBoundary>
              )}
            >
              <Route path="/partner/dashboard" element={<PartnerDashboard />} />
              <Route path="/partner/profile" element={<PartnerProfile />} />
              <Route path="/partner/bookings" element={<PartnerBookings />} />
              <Route path="/partner/earnings" element={<PartnerEarnings />} />
            </Route>

            {/* Customer App (Phase 3) */}
            <Route
              path="/customer/login"
              element={(
                <ErrorBoundary scope="customer" homePath="/">
                  <CustomerLogin />
                </ErrorBoundary>
              )}
            />
            <Route
              path="/customer/signup"
              element={(
                <ErrorBoundary scope="customer" homePath="/">
                  <CustomerSignup />
                </ErrorBoundary>
              )}
            />
            <Route path="/customer/payment/success" element={<PaymentResult outcome="success" />} />
            <Route path="/customer/payment/cancel" element={<PaymentResult outcome="cancel" />} />
            <Route
              element={(
                <ErrorBoundary scope="customer" homePath="/customer/login">
                  <ProtectedCustomerRoute>
                    <CustomerLayout />
                  </ProtectedCustomerRoute>
                </ErrorBoundary>
              )}
            >
              <Route path="/customer" element={<CustomerHome />} />
              <Route path="/customer/search" element={<CustomerHome />} />
              <Route path="/customer/partner/:id" element={<CustomerPartnerProfile />} />
              <Route path="/customer/booking/:partnerId/:serviceId" element={<CustomerBookingPage />} />
              <Route path="/customer/bookings" element={<CustomerBookings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
